import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { IoChevronBack } from 'react-icons/io5';
import Header from '../../components/Header/Header';
import StatCard from '../../components/StatCard/StatCard';
import SimCarrierDistribution from '../../components/SimCarrierDistribution/SimCarrierDistribution';
import DevicesByDistrict from '../../components/DevicesByDistrict/DevicesByDistrict';
import DataUsage from '../../components/DataUsage/DataUsage';
import MapDevicesPanel from '../../components/MapDevicesPanel/MapDevicesPanel';
import DataTable from '../../components/DataTable/DataTable';
import ExportExcelButton from '../../components/ExportExcelButton/ExportExcelButton';
import BarChartCard from '../../components/BarChartCard/BarChartCard';
import { useCircuitData } from '../../hooks/useCircuitData';
import { unslugify } from '../../data/districtData';
import { ENDPOINTS, timeAgo } from '../../api/api';
import { StatCardSkeleton, SectionSkeleton, BarChartSkeleton } from '../../components/Skeleton/Skeleton';
import styles from './Circuit.module.css';

const makeDeviceColumns = (navigate, districtSlug, circuitSlug) => [
  {
    key: 'imeiNumber',
    label: 'IMEI',
    render: (v) => (
      <span
        onClick={() => navigate(`/district/${districtSlug}/circuit/${circuitSlug}/device/${v}`)}
        style={{ color: 'var(--color-primary)', fontFamily: 'monospace', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' }}
      >
        {v}
      </span>
    ),
  },
  { key: 'name',         label: 'Name' },
  { key: 'manufacturer', label: 'Device' },
  { key: 'model',        label: 'Model' },
  { key: 'carrier',      label: 'Network provider' },
  { key: 'lastReportedTime', label: 'Last seen', render: timeAgo },
];

function Circuit() {
  const { districtSlug, circuitSlug } = useParams();
  const navigate     = useNavigate();
  const districtName = unslugify(districtSlug);
  const circuitName  = unslugify(circuitSlug);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { loading, devicesLoading, stats = {}, schools = [], byGrade = [], carriers = [], dataUsage = [], devices = [], totalPages = 1, refresh } = useCircuitData(circuitName, page, search);

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  const breadcrumbs = [
    { label: 'Overview',   path: '/' },
    { label: districtName, path: `/district/${districtSlug}` },
    { label: 'Circuit' },
  ];

  return (
    <>
      <Header breadcrumbs={breadcrumbs} />
      <div className={styles.page}>

        <div className={styles.titleRow}>
          <div className={styles.titleLeft}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
              <IoChevronBack size={16} />
            </button>
            <h1 className={styles.pageTitle}>{circuitName}</h1>
          </div>
          <div className={styles.titleActions}>
            <RefreshButton onClick={refresh} loading={loading} />
          </div>
        </div>

        <div className={styles.districtMeta}>
          <div className={styles.districtMetaLabel}>District</div>
          <div className={styles.districtMetaValue}>{districtName}</div>
        </div>

        <div className={styles.statsGrid}>
          {loading
            ? [0, 1].map((i) => <StatCardSkeleton key={i} />)
            : [
                { label: 'Devices Deployed', value: stats.devices ?? '—' },
                { label: 'Schools',          value: stats.schools  ?? '—' },
              ].map((s, i) => <StatCard key={i} {...s} />)}
        </div>

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionTitle}>SIM Carrier Distribution</div>
          {loading ? <SectionSkeleton rows={3} /> : <SimCarrierDistribution carriers={carriers} />}
        </div>

        <div className={styles.twoCol}>
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionTitle}>Devices by School</div>
            {loading ? <SectionSkeleton rows={6} /> : (
              <DevicesByDistrict
                districts={schools}
                linkBasePath={`/district/${districtSlug}/circuit/${circuitSlug}/school`}
              />
            )}
          </div>
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionTitle}>Data Usage</div>
            {loading ? <SectionSkeleton rows={4} /> : <DataUsage data={dataUsage} />}
          </div>
        </div>

        <MapDevicesPanel circuit={circuitName} />

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>Device Details</div>
            <ExportExcelButton
              url={ENDPOINTS.circuitDevicesExport(circuitName.toUpperCase(), search)}
              fallbackFileName={`${circuitSlug}-devices.xlsx`}
              disabled={loading}
            />
          </div>
          <DataTable
            columns={makeDeviceColumns(navigate, districtSlug, circuitSlug)}
            rows={devices}
            searchable
            searchPlaceholder="Search device details"
            paginated
            pageSize={10}
            page={page}
            totalPages={totalPages}
            onSearchChange={handleSearch}
            onPageChange={setPage}
            loadingRows={devicesLoading}
          />
        </div>

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionTitle}>Devices by Grade</div>
          {loading
            ? <BarChartSkeleton height={280} />
            : byGrade.length > 0
              ? <BarChartCard data={byGrade} xKey="name" yKey="count" height={280} />
              : null}
        </div>

      </div>
    </>
  );
}

export default Circuit;
